import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Space,
  message,
  Typography,
  Divider,
  Row,
  Col,
  Table,
  InputNumber,
  Select,
  Popconfirm,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { optionService } from '../services/option';

const { Title, Text } = Typography;
const { Option } = Select;

interface CommissionRate {
  id: string;
  customerType: string;
  rate: number;
  description?: string;
}

const CommissionSettings: React.FC = () => {
  const [form] = Form.useForm();
  const [commissionRates, setCommissionRates] = useState<CommissionRate[]>([]);
  const [customerTypes, setCustomerTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editingValues, setEditingValues] = useState<Partial<CommissionRate>>({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // 加载客户等级
      const customerTypesData = await optionService.get('customer_types', []);
      setCustomerTypes(Array.isArray(customerTypesData) ? customerTypesData : []);

      // 加载提成比例设置
      const commissionData = await optionService.get('commission_rates', []);

      // 如果是旧格式（字符串数组），转换为新格式
      if (Array.isArray(commissionData) && commissionData.length > 0) {
        if (typeof commissionData[0] === 'string') {
          // 旧格式，转换为新格式
          const newFormat: CommissionRate[] = commissionData.map((rate, index) => ({
            id: `rate_${index}`,
            customerType: 'A', // 默认客户等级
            rate: parseFloat(rate) || 0,
            description: ''
          }));
          setCommissionRates(newFormat);
        } else {
          // 新格式
          setCommissionRates(commissionData);
        }
      } else {
        setCommissionRates([]);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      message.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  const saveCommissionRates = async (rates: CommissionRate[]) => {
    try {
      setLoading(true);
      await optionService.set('commission_rates', rates);
      setCommissionRates(rates);
      message.success('保存成功');
    } catch (error) {
      message.error('保存失败');
      console.error('Failed to save commission rates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (values: { customerType: string; rate: number; description?: string }) => {
    // 检查是否已存在该客户等级的设置
    if (commissionRates.find(item => item.customerType === values.customerType)) {
      message.warning(`客户等级 "${values.customerType}" 已存在提成设置`);
      return;
    }

    const newRate: CommissionRate = {
      id: `rate_${Date.now()}`,
      customerType: values.customerType,
      rate: values.rate,
      description: values.description || ''
    };

    const newRates = [...commissionRates, newRate];
    await saveCommissionRates(newRates);
    form.resetFields();
  };

  const handleDelete = async (id: string) => {
    const newRates = commissionRates.filter(item => item.id !== id);
    await saveCommissionRates(newRates);
  };

  const handleEdit = (record: CommissionRate) => {
    setEditingKey(record.id);
    setEditingValues({ ...record });
  };

  const handleSave = async (id: string) => {
    const newRates = commissionRates.map(item => {
      if (item.id === id) {
        return { ...item, ...editingValues };
      }
      return item;
    });

    await saveCommissionRates(newRates);
    setEditingKey(null);
    setEditingValues({});
  };

  const handleCancel = () => {
    setEditingKey(null);
    setEditingValues({});
  };

  const columns = [
    {
      title: '客户等级',
      dataIndex: 'customerType',
      key: 'customerType',
      width: 150,
      render: (text: string, record: CommissionRate) => {
        if (editingKey === record.id) {
          return (
            <Select
              value={editingValues.customerType ?? text}
              style={{ width: '100%' }}
              onChange={(value) => setEditingValues({ ...editingValues, customerType: value })}
            >
              {customerTypes.map(type => (
                <Option key={type} value={type}>{type}</Option>
              ))}
            </Select>
          );
        }
        return <strong>{text}</strong>;
      },
    },
    {
      title: '提成比例',
      dataIndex: 'rate',
      key: 'rate',
      width: 150,
      render: (rate: number, record: CommissionRate) => {
        if (editingKey === record.id) {
          return (
            <InputNumber
              value={editingValues.rate ?? rate}
              min={0}
              max={100}
              precision={2}
              style={{ width: '100%' }}
              addonAfter="%"
              onChange={(value) => {
                if (typeof value === 'number' && !isNaN(value)) {
                  setEditingValues({ ...editingValues, rate: value });
                }
              }}
            />
          );
        }
        return <span style={{ color: '#1890ff', fontWeight: 'bold' }}>{rate}%</span>;
      },
    },
    {
      title: '说明',
      dataIndex: 'description',
      key: 'description',
      render: (text: string, record: CommissionRate) => {
        if (editingKey === record.id) {
          return (
            <Input
              value={editingValues.description ?? text}
              placeholder="可选说明"
              onChange={(e) => setEditingValues({ ...editingValues, description: e.target.value })}
            />
          );
        }
        return text || <Text type="secondary">无</Text>;
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_: any, record: CommissionRate) => {
        if (editingKey === record.id) {
          return (
            <Space>
              <Button size="small" type="primary" onClick={() => handleSave(record.id)}>
                保存
              </Button>
              <Button size="small" onClick={handleCancel}>
                取消
              </Button>
            </Space>
          );
        }

        return (
          <Space>
            <Button size="small" type="link" onClick={() => handleEdit(record)}>
              编辑
            </Button>
            <Popconfirm
              title="确定删除此提成设置吗？"
              onConfirm={() => handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button size="small" type="link" danger icon={<DeleteOutlined />}>
                删除
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const availableCustomerTypes = customerTypes.filter(
    type => !commissionRates.find(item => item.customerType === type)
  );

  return (
    <div>
      <Title level={4}>提成比例设置</Title>
      <Text type="secondary">按客户等级设置不同的提成比例，用于计算销售提成</Text>
      <Divider />

      {customerTypes.length === 0 && (
        <Card style={{ marginBottom: 16 }}>
          <Text type="warning">
            请先在"客户等级"标签页中设置客户等级选项（如：A、B、C、D），然后再配置提成比例
          </Text>
        </Card>
      )}

      <Row gutter={16}>
        <Col span={12}>
          <Card title="添加新的提成设置" size="small">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleAdd}
              disabled={loading || customerTypes.length === 0}
            >
              <Form.Item
                name="customerType"
                label="客户等级"
                rules={[{ required: true, message: '请选择客户等级' }]}
              >
                <Select placeholder="请选择客户等级">
                  {availableCustomerTypes.map(type => (
                    <Option key={type} value={type}>{type}</Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="rate"
                label="提成比例 (%)"
                rules={[
                  { required: true, message: '请输入提成比例' },
                  { type: 'number', min: 0, max: 100, message: '提成比例应在0-100之间' }
                ]}
              >
                <InputNumber
                  min={0}
                  max={100}
                  precision={2}
                  style={{ width: '100%' }}
                  placeholder="例如：10.5"
                  addonAfter="%"
                />
              </Form.Item>

              <Form.Item
                name="description"
                label="说明"
              >
                <Input placeholder="可选的说明信息" />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<PlusOutlined />}
                  loading={loading}
                  disabled={availableCustomerTypes.length === 0}
                  block
                >
                  添加设置
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        <Col span={12}>
          <Card title={`当前提成设置 (${commissionRates.length})`} size="small">
            <Table
              columns={columns}
              dataSource={commissionRates}
              rowKey="id"
              pagination={false}
              size="small"
              locale={{ emptyText: '暂无提成设置，请添加' }}
              loading={loading}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default CommissionSettings;
